import { createMultifileUploadSlice, speedStream } from "@nova/slice-multifile-upload"
import { createSlice } from "integrations/redux"
import { withRoot } from "@nova/with-root"
import {
    AnyAction,
    hasLRFTags,
    ImageProcessingState,
    ImageWarnings,
    PreviouslyParsedTagValues,
    ID,
    BoundingBox,
    isUsable,
    JobStatusType,
    SidePanelLayerType,
    ImageType,
} from "@nova/constants"
import { memoize, last } from "nodash"
import { createSelector } from "@reduxjs/toolkit"
import { OrthoImage } from "@nova/protobuf"
import {
    selectFlightOrthoBoundingBox,
    selectOrthoFlightById,
    selectOrthoFlightsMap,
    selectShowLayerByIdMapForFlightPaths,
    selectVisibleOrthoFlights,
} from "features/entity-caching/flights/selectors"
import { createOrthoImage } from "features/pubsub/entityActions/images/ortho"
import { dateAsNumber } from "@nova/serializer/src/client/util"
import { selectShowLayerByIdMapForHotspotOrthoOverlaps } from "features/map/layers/selectors"
import { calculateBoundingBoxCornerPoints, GeospatialPoint } from "@nova/calculate-geospatial"
import { BoundingBoxLayerData } from "plugins/shared/layers/BoundingBoxLayer.class"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import store from "integrations/redux/store"
import { OverlapLayerBoundingBoxLayerData } from "features/entity-caching/flights/types"
import { createIdFromFile, getEnumValue } from "@nova/util-strings"
import { addFlightUsage } from "features/data-management/workspace/subscriptions/module"
import { loadProject } from "features/map/control/thunks"
import {
    selectLastSelectedLayerId,
    selectSelectedLayers,
} from "panels/content/side-panel/slice.new"
import { updateEntitySocket } from "features/entity-caching/project/slice"
import { setGlobalNotificationContext } from "user/profile/slice"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { getNumberOfWorkers, runTask } from "@nova/webworker-pool"
import { type ImageMetadata, processImage } from "integrations/sensortool/processImage"
import {
    convertImageBufferWithCsv,
    csvConversionKind,
    drainPendingCsvDrop,
    getFlightCsv,
    makeAttachCsvFiles,
} from "features/job-management/uploader/mma-telemetry"
import { selectAllEntitiesMap } from "panels/content/side-panel/slice"
import { getAncestors } from "panels/content/side-panel/utils"
import { fileManagementApi } from "features/data-management/files/fileManagementApi"
import { IngestDataType } from "@nova/schemas"
import { createSpeedCallback } from "themes/default-tailwind/panels/GlobalUploadContainer"
import { AutoProcessController } from "panels/content/side-panel/upload-flow/preview-files/AutoProcessController"

type OrthoMeta = {
    organizationID: string
    fireID: string
    flightID: string
    setID: string
    file: File
}

export type OrthoState = Partial<OrthoImage> & {
    state: ImageProcessingState
    id: string
    warnings?: ImageWarnings
    deletedAt?: AnyAction
    tags?: Partial<PreviouslyParsedTagValues>
    hasLRF?: boolean
    projectedBbox?: number[][]
    hasThermalData?: boolean
    flightId?: string
    fileName?: string
    // Staged via LoadFromLayer's stageRemote (no backing File) — watched by
    // useImportArrivalWatcher to flip to IngestDone once the real entity lands.
    isRemoteImport?: boolean
}

const {
    actions,
    reducer: imagesReducer,
    selectors: {
        selectAllSets,
        selectAllUploads,
        selectSet,
        selectUpload,
        selectUploadsForSet,
        selectUploadIdsForSet,
        selectCancelledUploadIdsForSet,
        selectUploadsWithStates,
        selectUploadsForSetSummary: baseSelectUploadsForSetSummary,
        selectPendingForSet: baseSelectPendingForSet,
    },
} = createMultifileUploadSlice<"orthomosaic-images-v1", OrthoState, OrthoMeta>(
    "orthomosaic-images-v1",
    (m: OrthoMeta) => {
        // .owi (RGB) is demosaiced/graded and converted to a JPEG before upload.
        // Give it a .jpg ID so the S3 key and entity record use the right
        // extension from the start.
        const rawId = createIdFromFile(m.file)
        const id = csvConversionKind(m.file.name) ? rawId.replace(/\.(png|owi)$/i, ".jpg") : rawId
        return {
            id,
            state: ImageProcessingState.Pending,
            fileName: m.file.name,
        } satisfies OrthoState
    },
    {
        addUploadToProject: (setId, upload) => {
            delete upload.tags
            delete upload.flightId
            delete upload.fileName
            return createOrthoImage({
                data: upload as OrthoImage,
                context: { flightId: setId },
            })
        },
    },
    async (state, meta, update, { useSignal, useSemaphore }) => {
        const uploadSignal = useSignal("upload", meta.setID)
        const deleteSignal = useSignal("delete", state.id)
        deleteSignal.then(() => {
            if (
                state.state !== ImageProcessingState.UploadDone &&
                state.state !== ImageProcessingState.IngestDone
            )
                state = { ...state, deletedAt: new Date().toISOString() }
        })
        let metadata: ImageMetadata
        // .owi (RGB) is converted to TIFF via MMA telemetry before upload; jpg is
        // uploaded as-is.
        const conversionKind = csvConversionKind(meta.file.name)
        // Converted image buffer — set during processing for .owi, used during upload.
        let tiffBuffer: ArrayBuffer | undefined
        let hadInvalidTelemetry = false
        // semaphore lock - maximum 15 pending to make sure we don't overload the machine, RGB images take a bit to chunk through
        await useSemaphore(getNumberOfWorkers() * 3, async () => {
            update({ ...state, state: ImageProcessingState.Processing })
            if (state.deletedAt) return
            // process the data
            const fileBuffer = await meta.file.arrayBuffer()

            if (conversionKind) {
                // Wait for attachCsvFiles to populate the registry and fire the csv
                // signal, unless telemetry was already attached for this flight.
                if (!getFlightCsv(meta.setID)) {
                    await useSignal("csv", meta.setID)
                }
                if (state.deletedAt) return

                const result = await convertImageBufferWithCsv(
                    meta.file.name,
                    fileBuffer,
                    meta.setID
                )
                if (result.status !== "ok" || state.deletedAt) {
                    if (!state.deletedAt) {
                        hadInvalidTelemetry = true
                        update({
                            ...state,
                            error: { name: "InvalidTelemetry", message: "No matching CSV row" },
                        } as typeof state)
                    }
                    return
                }
                tiffBuffer = result.buffer

                // Run the generated image through sensortool so metadata extraction
                // uses the same canonical path as every other image type.
                metadata = await runTask(processImage, { fileBuffer: tiffBuffer })
            } else {
                metadata = await runTask(processImage, { fileBuffer }, { transfer: [fileBuffer] })
            }

            if (state.deletedAt) return

            state = {
                ...state,
                ...metadata?.gps,
                flightId: meta.setID,
                takenAt: dateAsNumber(metadata?.takenAt),
                state: ImageProcessingState.ProcessingDone,
                tags: metadata?.tags,
                hasLRF: hasLRFTags(metadata?.tags),
                pitch: metadata?.pyr?.pitch || -90,
                yaw: metadata?.pyr?.yaw || 0,
                roll: metadata?.pyr?.roll || 0,
                height: metadata?.height,
                fov: Array.from(metadata?.fov || []),
                make: metadata?.make,
                model: metadata?.model,
                focalLength: metadata?.focalLength,
                size: meta.file.size,
                imageHeight: metadata?.imageHeight,
                imageWidth: metadata?.imageWidth,
                imageType: conversionKind
                    ? ImageType.OWI
                    : getEnumValue(ImageType, last(meta.file.name.split(".")) || ""),
                hasThermalData: metadata?.hasThermalData,
                fileName: meta.file.name,
            }
            update(state)
        })

        if (hadInvalidTelemetry) return

        // this waits for the upload action to be fired by the UI
        if (state.deletedAt) return
        await uploadSignal
        if (state.deletedAt) return
        state = {
            ...state,
            state: ImageProcessingState.PendingUpload,
        }
        update(state)

        // semaphore lock - revisit https://en.wikipedia.org/wiki/Semaphore_(programming) before poking around it this
        // 15 is the number of uploads allowed in-flight at once. The slot is released as soon as S3 confirms the upload
        // (onS3Complete), not after ingest finishes, so slow ingest doesn't starve the upload queue.
        let ingestPromise: Promise<unknown> | undefined
        let hadIngestError = false
        await useSemaphore(15, async () => {
            if (state.deletedAt) return
            update({ ...state, state: ImageProcessingState.Uploading })

            let s3Done!: () => void
            const s3Signal = new Promise<void>((r) => (s3Done = r))

            ingestPromise = fileManagementApi.putFile(
                tiffBuffer ? new Uint8Array(tiffBuffer) : meta.file,
                {
                    filename: state.id,
                    tags: {
                        dataType: IngestDataType.OrthoImage,
                        projectId: meta.fireID,
                        orgId: meta.organizationID,
                        flightId: meta.flightID,
                    },
                    onProgress: createSpeedCallback(({ speedMbps }) => {
                        speedStream.setSpeed(meta.setID, state.id, speedMbps)
                    }),
                    onS3Complete: () => {
                        if (!state.deletedAt) {
                            update({ ...state, state: ImageProcessingState.UploadDone })
                        }
                        s3Done()
                    },
                    maxQueue: 5,
                    onIngestError: (ingest) => {
                        hadIngestError = true
                        update({
                            ...state,
                            error: { name: "IngestError", message: `ingest ${ingest.status}` },
                        } as typeof state)
                    },
                }
            )

            // Release semaphore after S3 upload; if upload errors, release immediately via the race
            await Promise.race([s3Signal, ingestPromise.catch(() => {})])
        })

        if (ingestPromise) {
            await ingestPromise
            if (!state.deletedAt && !hadIngestError) {
                update({ ...state, state: ImageProcessingState.IngestDone }, () => {
                    AutoProcessController.emit(`flightId:${meta.setID}:ingest-progress`)
                })
            }
        }

        return { ...state }
    },
    () => ({
        warnings: undefined,
    })
)

const stageFiles = (
    organizationID: string,
    fireID: string,
    flightID: string,
    files: File[],
    onFinish?: () => void
) => {
    // Drain any CSV-only drop that fileFilter set immediately before this call
    const csvFiles = drainPendingCsvDrop()
    if (csvFiles.length > 0) {
        store.dispatch(attachCsvFiles(flightID, csvFiles))
    }

    const meta = new Array<OrthoMeta>(files.length)
    let totalSize = 0
    for (let i = 0; i < files.length; i++) {
        meta[i] = {
            organizationID,
            fireID,
            flightID,
            setID: flightID,
            file: files[i],
        }
        totalSize += files[i].size
    }
    addFlightUsage(flightID, totalSize)
    return actions.stageFiles({ meta, onFinish })
}

const uploadFlight = (id: string) => actions.signal("upload", id)
const deleteUpload = actions.deleteUpload
const deleteUploadSet = actions.deleteUploadSet
const stageRemote = actions.stageRemote
const updateManyUploads = actions.updateMany

// this is summarizing the state of uploads for a set
// it's useful for determining if a set is uploading, how much of it is uploading, etc.
const selectUploadsForSetSummary = (id: string) =>
    baseSelectUploadsForSetSummary(
        id,
        (t) => t.state,
        (t) => t.state === ImageProcessingState.IngestDone
    )

const selectPendingUploadsForSet = (id: string) => baseSelectPendingForSet(id, "upload")

export const selectOrthoJobByFlightId = memoize((id: string) =>
    createSelector([selectOrthoFlightById(id)], (flight) => flight?.job)
)

type UIState = {
    activeOrthophotos: string[]
    visibleOrthoOverlapsById: Record<string, boolean>
    history: Record<
        string,
        {
            activeOrthophotos: string[]
            visibleOrthoOverlapsById: Record<string, boolean>
            splitButtonOpen: boolean
            statusCheckerOpen: boolean
            selectedUpload: Record<string, boolean>
        }
    >
    activeFire: string | undefined
    splitButtonOpen: boolean
    statusCheckerOpen: boolean
    selectedUpload: Record<string, boolean>

    // This holds the ortho overlap bounding box data for both RGB and IR orthos.
    orthoOverlapBoundingBoxesById: Record<string, [number, number][]>

    // 0-100
    orthoOpacityById: Record<string, number>

    // Flights that have MMA telemetry CSV attached (for .owi upload gating).
    csvFlights: Record<string, true>
}

const uiStateName = "orthomosaic-ui-v1"
type uiStateNameType = typeof uiStateName

const initialState = {
    activeOrthophotos: [] as string[],
    visibleOrthoOverlapsById: {},
    history: {} as Record<
        string,
        {
            activeOrthophotos: string[]
            visibleOrthoOverlapsById: Record<string, boolean>
            splitButtonOpen: boolean
            statusCheckerOpen: boolean
            selectedUpload: Record<string, boolean>
        }
    >,
    splitButtonOpen: false,
    selectedUpload: {},
    statusCheckerOpen: false,
    orthoOverlapBoundingBoxesById: {},
    orthoOpacityById: {},
    csvFlights: {},
} as UIState

const {
    reducer: uiStateReducer,
    actions: {
        toggleActiveOrthophoto,
        activateOrthophoto,
        setOrthoOverlapLayer,
        setSplitButtonOpen,
        setStatusCheckerOpen,
        setSelectedUpload,
        setOrthoOverlapBoundingBox,
        setOrthoOpacity,
        setCsvFlight,
    },
} = createSlice({
    name: uiStateName,
    initialState: {
        ...initialState,
    },
    reducers: {
        toggleActiveOrthophoto: (state, action: { payload: string }) => {
            const idx = state.activeOrthophotos.indexOf(action.payload)
            if (idx !== -1) state.activeOrthophotos.splice(idx, 1)
            else state.activeOrthophotos.push(action.payload)
            if (state.activeFire)
                state.history[state.activeFire] = {
                    activeOrthophotos: [...state.activeOrthophotos],
                    visibleOrthoOverlapsById: { ...state.visibleOrthoOverlapsById },
                    splitButtonOpen: state.splitButtonOpen,
                    selectedUpload: state.selectedUpload,
                    statusCheckerOpen: state.statusCheckerOpen,
                }
        },
        activateOrthophoto: (state, action: { payload: string }) => {
            const isAlreadyActive = state.activeOrthophotos.indexOf(action.payload) !== -1
            if (isAlreadyActive) return
            state.activeOrthophotos.push(action.payload)
            if (state.activeFire)
                state.history[state.activeFire] = {
                    activeOrthophotos: [...state.activeOrthophotos],
                    visibleOrthoOverlapsById: { ...state.visibleOrthoOverlapsById },
                    splitButtonOpen: state.splitButtonOpen,
                    selectedUpload: state.selectedUpload,
                    statusCheckerOpen: state.statusCheckerOpen,
                }
        },
        setOrthoOverlapLayer: (state, action: { payload: { id: ID; active?: boolean } }) => {
            if (action.payload.active === undefined) {
                delete state.visibleOrthoOverlapsById[action.payload.id]
            } else {
                state.visibleOrthoOverlapsById[action.payload.id] = action.payload.active
            }
        },
        setOrthoOverlapBoundingBox: (
            state,
            action: { payload: OverlapLayerBoundingBoxLayerData }
        ) => {
            const { id, corners } = action.payload
            state.orthoOverlapBoundingBoxesById[id] = corners
        },
        setOrthoOpacity: (
            state,
            action: { payload: { id: string; updates: { opacity: number } } }
        ) => {
            const { id, updates } = action.payload
            state.orthoOpacityById[id] = updates.opacity
        },
        setSplitButtonOpen: (state, action) => {
            state.splitButtonOpen = action.payload
        },
        setStatusCheckerOpen: (state, action) => {
            state.statusCheckerOpen = action.payload
        },
        setSelectedUpload: (state, action) => {
            state.selectedUpload = action.payload
        },
        setCsvFlight: (state, action) => {
            state.csvFlights[action.payload] = true
        },
    },
    extraReducers: (b) =>
        b
            .addCase(loadProject.fulfilled, (state, action) => {
                action.payload.orthoFlights.forEach(({ id, archivedTimestamp, job }) => {
                    if (
                        state.activeOrthophotos.indexOf(id) === -1 &&
                        job &&
                        job.status >= JobStatusType.COMPLETED &&
                        !archivedTimestamp
                    )
                        state.activeOrthophotos.push(id)
                })
            })
            .addCase(updateEntitySocket, (state, action) => {
                const { updates, path } = action.payload
                const layerName = path[0]
                if (layerName !== "orthoFlights") return
                const { id } = path[1]
                if (updates.error) {
                    setTimeout(() => {
                        store.dispatch(
                            setGlobalNotificationContext({
                                message: `Orthomosaic failed to process. Try again with a revised dataset.`,
                                status: GlobalNotificationStatus.error,
                                timeoutInMs: 7000,
                            })
                        )
                    }, 0)
                } else if (updates.archivedTimestamp) {
                    if (state.activeOrthophotos.includes(id)) {
                        state.activeOrthophotos = state.activeOrthophotos.filter(
                            (photo) => photo !== id
                        )
                    }
                }
            }),
})

const {
    selectActiveOrthophotos,
    selectActiveOrthophotoForId,
    selectOrthoOverlapLayerIsVisible,
    selectOrthoOverlapLayerVisibilityById,
    selectSplitButtonOpen,
    selectStatusCheckerOpen,
    selectSelectedUpload,
    selectOrthoOverlapBoundingBoxMap,
    selectCsvFlights,
} = withRoot((r: Record<uiStateNameType, UIState>) => r[uiStateName], {
    selectActiveOrthophotos: () => (s) => s.activeOrthophotos,
    selectActiveOrthophotoForId: (id) => (s) => s.activeOrthophotos.includes(id),
    selectOrthoOverlapLayerIsVisible: (id) => (s) =>
        s.visibleOrthoOverlapsById[id] as boolean | undefined,
    selectOrthoOverlapLayerVisibilityById: () => (s) => s.visibleOrthoOverlapsById,
    selectSplitButtonOpen: () => (s) => s.splitButtonOpen,
    selectStatusCheckerOpen: () => (s) => s.statusCheckerOpen,
    selectSelectedUpload: () => (s) => s.selectedUpload,
    selectOrthoOverlapBoundingBoxMap: () => (s) => s.orthoOverlapBoundingBoxesById,
    selectCsvFlights: () => (s) => s.csvFlights,
})

// Parses dropped telemetry CSVs into the shared registry, flags the flight, and
// fires this slice's "csv" signal so .owi files waiting on telemetry can proceed.
const attachCsvFiles = makeAttachCsvFiles({
    markFlightHasCsv: setCsvFlight,
    signalCsvReady: (flightId: string) => actions.signal("csv", flightId),
})

export const selectVisibleActiveOrthophotos = createSelector(
    [selectActiveOrthophotos(), selectOrthoFlightsMap, selectAllEntitiesMap],
    (orthoIds, orthoById, entityMap) =>
        orthoIds.filter(
            (orthoId) =>
                orthoById[orthoId]?.visible &&
                !getAncestors(orthoId, entityMap).find((ancestor) => !ancestor.visible) &&
                !orthoById[orthoId].archivedTimestamp
        )
)

export const selectOrthoOverlapBoundingBoxById = memoize((flightId: string) =>
    createSelector(
        [selectOrthoOverlapBoundingBoxMap()],
        (boundingBoxById) => boundingBoxById[flightId]
    )
)

export const selectOrthoFlightIsBoundingBoxShowing = memoize((flightId: string) =>
    createSelector(
        [selectShowLayerByIdMapForFlightPaths, selectActiveOrthophotos()],
        (flightPathShowState, activeOrthos) => {
            if (!flightId) return false

            const showingFlightPath = flightPathShowState[flightId]
            const showingOrtho = activeOrthos.includes(flightId)
            return showingFlightPath || showingOrtho
        }
    )
)
export const mostRecentOrthoBoundingBoxMap: Record<ID, BoundingBox> = {}
export const selectOrthoBoundingBoxForFlightId = memoize((targetFlightId) =>
    createSelector(
        [
            selectFlightOrthoBoundingBox(targetFlightId),
            selectOrthoOverlapBoundingBoxById(targetFlightId),
            selectOrthoImagesAndUploadsByFlightId(targetFlightId),
        ],
        (orthoBoundingBox, orthoOverlapBoundingBox, images) => {
            const boundingBoxPoints: GeospatialPoint[] = []

            if (orthoBoundingBox) {
                boundingBoxPoints.push(...orthoBoundingBox)
            }

            if (orthoOverlapBoundingBox) {
                boundingBoxPoints.push(
                    ...orthoOverlapBoundingBox.map(
                        (corner) =>
                            ({ longitude: corner[0], latitude: corner[1] }) as GeospatialPoint
                    )
                )
            }

            if (images) {
                for (const i of images) {
                    const image = isUsable(i)
                    if (image?.longitude && image?.latitude && image?.takenAt) {
                        const { longitude, latitude } = image
                        boundingBoxPoints.push({
                            longitude,
                            latitude,
                        })
                    }
                }
            }

            if (boundingBoxPoints.length === 0) {
                return mostRecentOrthoBoundingBoxMap[targetFlightId]
            }
            const boundingBox = calculateBoundingBoxCornerPoints(boundingBoxPoints)
            mostRecentOrthoBoundingBoxMap[targetFlightId] = boundingBox
            return boundingBox
        }
    )
)

export const selectAllOrthoBoundingBoxes = createSelector(
    [selectVisibleOrthoFlights, selectAllUploads(), selectOrthoOverlapBoundingBoxMap()],
    (flights) => {
        const state = store.getState()
        const boxes: BoundingBoxLayerData[] = []
        Object.values(flights).forEach((flight) => {
            const box = selectOrthoBoundingBoxForFlightId(flight.id)(state)
            if (!box) return
            const polygon = box?.polygon || []
            const path = [...polygon, polygon[0]]

            boxes.push({ id: flight.id, polygon, path, area: box.area })
        })
        return boxes
    }
)

export const selectIsAnyOrthoOverlapShowing = createSelector(
    [selectOrthoOverlapLayerVisibilityById(), selectShowLayerByIdMapForHotspotOrthoOverlaps],
    (orthoOverlaps, IROrthoOverlaps) => {
        return (
            Object.values(orthoOverlaps).some((isShowing) => !!isShowing) ||
            Object.values(IROrthoOverlaps).some((isShowing) => !!isShowing)
        )
    }
)

const root = (state: AnyAction) => state[uiStateName] as UIState

export const selectOrthoOpacityMap = createSelector([root], (state) => state.orthoOpacityById)

export const selectOrthoOpacityById = memoize((flightId: string) =>
    createSelector([root], (state) => {
        const opacity = state.orthoOpacityById[flightId]
        return opacity === undefined ? 100 : opacity
    })
)

// export selectors
export {
    selectAllUploads,
    selectAllSets,
    selectSet,
    selectUpload,
    selectUploadsForSet,
    selectUploadIdsForSet,
    selectCancelledUploadIdsForSet,
    selectUploadsWithStates,
    selectPendingUploadsForSet,
    selectUploadsForSetSummary,
    selectActiveOrthophotos,
    selectSplitButtonOpen,
    selectSelectedUpload,
    selectStatusCheckerOpen,
    selectActiveOrthophotoForId,
    selectOrthoOverlapLayerIsVisible,
    selectOrthoOverlapLayerVisibilityById,
    selectOrthoOverlapBoundingBoxMap,
    selectCsvFlights,
}

// export actions
export {
    uploadFlight,
    deleteUpload,
    stageFiles,
    toggleActiveOrthophoto,
    activateOrthophoto,
    setOrthoOverlapLayer,
    setSplitButtonOpen,
    setSelectedUpload,
    setStatusCheckerOpen,
    setOrthoOverlapBoundingBox,
    setOrthoOpacity,
    deleteUploadSet,
    stageRemote,
    updateManyUploads,
}

// export reducer
export const reducer = {
    ...imagesReducer,
}
export const uiReducer = { [uiStateName]: uiStateReducer }

export const selectSelectedOrthoFlightId = createSelector(
    [selectLastSelectedLayerId, selectSelectedLayers],
    (lastSelectedLayerId, selectedLayers) =>
        selectedLayers.find((layer) => layer.id === lastSelectedLayerId)?.type ===
        SidePanelLayerType.OrthomosaicFlight
            ? lastSelectedLayerId
            : undefined
)
